
import { UsageRecord, StockUpdate } from '@/lib/types';
import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/utils/errorLogger';
import { getCurrentUserId } from '@/lib/supabase';

/**
 * Fetch usage records directly from Supabase for analytics
 */
export const fetchAnalyticsUsageRecords = async (startDate?: Date): Promise<UsageRecord[]> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for analytics data');
      return [];
    }

    let query = supabase
      .from('usage_records')
      .select('*')
      .eq('user_id', userId);
    
    // Apply date filter if provided
    if (startDate) {
      query = query.gte('date', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      logError({
        type: 'api',
        message: `Error fetching analytics usage records: ${error.message}`,
        source: 'fetchAnalyticsUsageRecords',
      });
      console.error('Error fetching analytics usage records:', error);
      return [];
    }

    // Map from database format to app format
    const records: UsageRecord[] = data.map(item => ({
      id: item.id,
      productId: item.product_id,
      quantity: item.quantity,
      date: new Date(item.date),
      outputVolume: item.output_volume,
      consistency: item.consistency as 'liquid' | 'semi-solid' | 'solid' | 'other' | undefined,
      color: item.color,
      notes: item.notes,
    }));

    console.log(`Fetched ${records.length} usage records directly from backend for analytics`);
    return records;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in fetchAnalyticsUsageRecords: ${error.message}`,
      source: 'fetchAnalyticsUsageRecords',
      stack: error.stack,
    });
    console.error('Error in fetchAnalyticsUsageRecords:', error);
    return [];
  }
};

/**
 * Fetch stock updates directly from Supabase for analytics
 */
export const fetchAnalyticsStockUpdates = async (startDate?: Date): Promise<StockUpdate[]> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for analytics stock data');
      return [];
    }

    let query = supabase
      .from('stock_updates')
      .select('*')
      .eq('user_id', userId);
    
    // Apply date filter if provided
    if (startDate) {
      query = query.gte('date', startDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      logError({
        type: 'api',
        message: `Error fetching analytics stock updates: ${error.message}`,
        source: 'fetchAnalyticsStockUpdates',
      });
      console.error('Error fetching analytics stock updates:', error);
      return [];
    }

    // Map from database format to app format
    const updates: StockUpdate[] = data.map(item => ({
      id: item.id,
      productId: item.product_id,
      quantity: item.quantity,
      date: new Date(item.date),
    }));

    console.log(`Fetched ${updates.length} stock updates directly from backend for analytics`);
    return updates;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in fetchAnalyticsStockUpdates: ${error.message}`,
      source: 'fetchAnalyticsStockUpdates',
      stack: error.stack,
    });
    console.error('Error in fetchAnalyticsStockUpdates:', error);
    return [];
  }
};

/**
 * Calculate usage rate for a product based on backend data
 */
export const calculateProductUsageRate = async (productId: string, days: number = 30): Promise<number> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for calculating usage rate');
      return 0;
    }

    // Calculate the start date based on the number of days
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    // Fetch usage records for this product in the specified period
    const { data, error } = await supabase
      .from('usage_records')
      .select('quantity, date')
      .eq('user_id', userId)
      .eq('product_id', productId)
      .gte('date', startDate.toISOString());

    if (error) {
      logError({
        type: 'api',
        message: `Error calculating usage rate: ${error.message}`,
        source: 'calculateProductUsageRate',
      });
      console.error('Error calculating usage rate:', error);
      return 0;
    }

    if (data.length === 0) return 0;

    // Calculate total usage
    const totalUsage = data.reduce((sum, record) => sum + record.quantity, 0);
    
    // Calculate usage rate (units per day)
    const usageRate = totalUsage / days;
    
    console.log(`Calculated usage rate for product ${productId}: ${usageRate.toFixed(2)} units/day over ${days} days`);
    return usageRate;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in calculateProductUsageRate: ${error.message}`,
      source: 'calculateProductUsageRate',
      stack: error.stack,
    });
    console.error('Error in calculateProductUsageRate:', error);
    return 0;
  }
};
